/**
 * SpiralOverlay — Restrained Pivot Marker
 * ---------------------------------------------------------------
 * The conversation shifted. No flash, no amber blast — a quiet
 * dim, a held line, a small archival mark. Forensic, not loud.
 */

import { AbsoluteFill, useCurrentFrame, interpolate } from "remotion";
import { PALETTE } from "../utils/timing";

interface Props {
  spiralFrame: number;
  holdFrames?: number;
}

export const SpiralOverlay: React.FC<Props> = ({ spiralFrame, holdFrames = 54 }) => {
  const frame = useCurrentFrame();
  if (spiralFrame < 0) return null;

  const local = frame - spiralFrame;
  if (local < -2 || local > holdFrames + 36) return null;

  // Slow fade-in / fade-out — no spring
  const enter = interpolate(local, [0, 24], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const exit = interpolate(local, [holdFrames, holdFrames + 30], [1, 0], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const opacity = enter * exit;

  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity }}>
      {/* Quiet ambient dim — no flash */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.35) 80%)",
        }}
      />
      {/* Restrained gold band */}
      <div
        style={{
          position: "absolute",
          top: "46%",
          left: 60,
          right: 60,
          padding: "18px 0",
          borderTop: `1px solid ${PALETTE.goldSoft}`,
          borderBottom: `1px solid ${PALETTE.goldSoft}`,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          color: PALETTE.gold,
          fontSize: 22,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          textAlign: "center",
          opacity: 0.85,
        }}
      >
        Conversation shifted
      </div>
      {/* Sub-mark */}
      <div
        style={{
          position: "absolute",
          top: "53%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          color: PALETTE.textDim,
          fontSize: 13,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          opacity: 0.7,
        }}
      >
        Pivot · observed
      </div>
    </AbsoluteFill>
  );
};
